import { useEffect } from 'react';
import { useLoggedViewer } from '@/api/viewer/use-viewer';
import useViewerRoleSafe from '@/lib/rights/use-viewer-role-safe';
import { useDatadogRumActions } from './use-datadog-rum';
import { useParams } from 'react-router-dom';
import useShop from '@babackapp/private-api-client/client/use-shop';
import { useAuth } from "@babackapp/auth-client/context";

export function useDatadogAuthTracking() {
  const { setUser, addAction, setGlobalContext } = useDatadogRumActions();
  const viewerRole = useViewerRoleSafe();
  const { error: authError, user } = useAuth();
  const { shopId } = useParams<{ shopId: string }>();
  const { data: currentShop } = useShop(shopId);

  useEffect(() => {
    if (user) {
      setUser({
        id: user.id,
        name: user.profile?.firstName && user.profile?.lastName
          ? `${user.profile.firstName} ${user.profile.lastName}`
          : user.emails?.[0]?.address,
        email: user.emails?.[0]?.address,
        role: viewerRole || 'unknown',
      });

      setGlobalContext('user.verified', !!user.emails?.[0]?.verified);
      setGlobalContext('user.language', user.profile?.lang || 'unknown');

      if (currentShop) {
        setGlobalContext('shop.id', currentShop.id);
        setGlobalContext('shop.name', currentShop.name);
      }

      addAction('auth.user_loaded', {
        userId: user.id,
        userRole: viewerRole || 'no_shop_context',
        shopId: currentShop?.id,
        verified: !!user.emails?.[0]?.verified,
      });
    }
  }, [user, viewerRole, currentShop, setUser, addAction, setGlobalContext]);

  useEffect(() => {
    if (authError) {
      addAction('auth.error', {
        error: authError,
        context: 'user_load_failed',
      });
    }
  }, [authError, addAction]);

  return {
    isUserTracked: !!user,
    user,
    role: viewerRole,
    hasShopContext: !!currentShop,
  };
}
